import { consoleApiSlice } from "./consoleApiSlice";

export const scanTemplateSyncApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Get staging templates (via Console backend)
    getStagingTemplates: builder.query({
      query: ({ websiteId, templateType, category, planType, skip = 0, limit = 100 }) => {
        const params = { skip, limit };
        if (websiteId) params.website_id = websiteId;
        if (templateType) params.template_type = templateType;
        if (category) params.category = category;
        if (planType) params.plan_type = planType;

        return {
          url: "/api/v1/scan_templates_v2/staging",
          params,
        };
      },
      providesTags: ["StagingTemplates"],
    }),

    // Get prod templates (via Console backend)
    getProdTemplates: builder.query({
      query: ({ websiteId, templateType, category, planType, skip = 0, limit = 100 }) => {
        const params = { skip, limit };
        if (websiteId) params.website_id = websiteId;
        if (templateType) params.template_type = templateType;
        if (category) params.category = category;
        if (planType) params.plan_type = planType;

        return {
          url: "/api/v1/scan_templates_v2/prod",
          params,
        };
      },
      providesTags: ["ProdTemplates"],
    }),

    // Get staging template detail
    getStagingTemplateDetail: builder.query({
      query: ({ templateId, websiteId }) => {
        const params = {};
        if (websiteId) params.website_id = websiteId;

        return {
          url: `/api/v1/scan_templates_v2/staging/${templateId}`,
          params,
        };
      },
      providesTags: (result, error, { templateId }) => [
        { type: "StagingTemplates", id: templateId },
      ],
    }),

    // Get prod template detail
    getProdTemplateDetail: builder.query({
      query: ({ templateId, websiteId }) => {
        const params = {};
        if (websiteId) params.website_id = websiteId;

        return {
          url: `/api/v1/scan_templates_v2/prod/${templateId}`,
          params,
        };
      },
      providesTags: (result, error, { templateId }) => [
        { type: "ProdTemplates", id: templateId },
      ],
    }),

    // Get sync status
    getSyncStatus: builder.query({
      query: ({ templateIds }) => {
        const params = {};
        if (templateIds && templateIds.length > 0) {
          params.template_ids = templateIds.join(",");
        }

        return {
          url: "/api/v1/scan_templates_v2/sync-status",
          params,
        };
      },
      providesTags: ["SyncStatus"],
    }),

    // Sync staging to prod
    syncStagingToProd: builder.mutation({
      query: ({ templateIds }) => ({
        url: "/api/v1/scan_templates_v2/sync/staging-to-prod",
        method: "POST",
        body: { template_ids: templateIds },
      }),
      invalidatesTags: ["SyncStatus", "ProdTemplates", "StagingTemplates"],
    }),

    // Sync prod to staging
    syncProdToStaging: builder.mutation({
      query: ({ templateIds }) => ({
        url: "/api/v1/scan_templates_v2/sync/prod-to-staging",
        method: "POST",
        body: { template_ids: templateIds },
      }),
      invalidatesTags: ["SyncStatus", "ProdTemplates", "StagingTemplates"],
    }),

    // Create staging prompt
    createStagingPrompt: builder.mutation({
      query: ({ templateId, prompt }) => ({
        url: `/api/v1/scan_templates_v2/staging/${templateId}/prompts`,
        method: "POST",
        body: prompt,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "StagingTemplates", id: templateId },
        "SyncStatus",
      ],
    }),

    // Create prod prompt
    createProdPrompt: builder.mutation({
      query: ({ templateId, prompt }) => ({
        url: `/api/v1/scan_templates_v2/prod/${templateId}/prompts`,
        method: "POST",
        body: prompt,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "ProdTemplates", id: templateId },
        "SyncStatus",
      ],
    }),

    // Create staging prompt version
    createStagingPromptVersion: builder.mutation({
      query: ({ promptId, version }) => ({
        url: `/api/v1/scan_templates_v2/staging/prompts/${promptId}/versions`,
        method: "POST",
        body: version,
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    // Create prod prompt version
    createProdPromptVersion: builder.mutation({
      query: ({ promptId, version }) => ({
        url: `/api/v1/scan_templates_v2/prod/prompts/${promptId}/versions`,
        method: "POST",
        body: version,
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    // Pin staging version
    pinStagingVersion: builder.mutation({
      query: ({ promptId, versionId }) => ({
        url: `/api/v1/scan_templates_v2/staging/prompts/${promptId}/pin-version`,
        method: "PUT",
        params: { version_id: versionId },
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    // Pin prod version
    pinProdVersion: builder.mutation({
      query: ({ promptId, versionId }) => ({
        url: `/api/v1/scan_templates_v2/prod/prompts/${promptId}/pin-version`,
        method: "PUT",
        params: { version_id: versionId },
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    // Update staging template metadata
    updateStagingTemplate: builder.mutation({
      query: ({ templateId, updates }) => ({
        url: `/api/v1/scan_templates_v2/staging/${templateId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "StagingTemplates", id: templateId },
        "StagingTemplates",
        "SyncStatus",
      ],
    }),

    // Update prod template metadata
    updateProdTemplate: builder.mutation({
      query: ({ templateId, updates }) => ({
        url: `/api/v1/scan_templates_v2/prod/${templateId}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "ProdTemplates", id: templateId },
        "ProdTemplates",
        "SyncStatus",
      ],
    }),

    // Create staging template
    createStagingTemplate: builder.mutation({
      query: (template) => ({
        url: `/api/v1/scan_templates_v2/staging`,
        method: "POST",
        body: template,
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    // Create prod template
    createProdTemplate: builder.mutation({
      query: (template) => ({
        url: `/api/v1/scan_templates_v2/prod`,
        method: "POST",
        body: template,
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    // Delete staging template
    deleteStagingTemplate: builder.mutation({
      query: ({ templateId }) => ({
        url: `/api/v1/scan_templates_v2/staging/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    // Delete prod template
    deleteProdTemplate: builder.mutation({
      query: ({ templateId }) => ({
        url: `/api/v1/scan_templates_v2/prod/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    createStagingTestCases: builder.mutation({
      query: ({ templateId, testCases }) => ({
        url: `/api/v1/scan_templates_v2/staging/${templateId}/test-cases`,
        method: "POST",
        body: testCases,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "StagingTemplates", id: templateId },
        "SyncStatus",
      ],
    }),

    createProdTestCases: builder.mutation({
      query: ({ templateId, testCases }) => ({
        url: `/api/v1/scan_templates_v2/prod/${templateId}/test-cases`,
        method: "POST",
        body: testCases,
      }),
      invalidatesTags: (result, error, { templateId }) => [
        { type: "ProdTemplates", id: templateId },
        "SyncStatus",
      ],
    }),

    createStagingTestCasesVersion: builder.mutation({
      query: ({ containerId, version }) => ({
        url: `/api/v1/scan_templates_v2/staging/test-cases/${containerId}/versions`,
        method: "POST",
        body: version,
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    createProdTestCasesVersion: builder.mutation({
      query: ({ containerId, version }) => ({
        url: `/api/v1/scan_templates_v2/prod/test-cases/${containerId}/versions`,
        method: "POST",
        body: version,
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    pinStagingTestCasesVersion: builder.mutation({
      query: ({ containerId, versionId }) => ({
        url: `/api/v1/scan_templates_v2/staging/test-cases/${containerId}/pin-version`,
        method: "PUT",
        params: { version_id: versionId },
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    pinProdTestCasesVersion: builder.mutation({
      query: ({ containerId, versionId }) => ({
        url: `/api/v1/scan_templates_v2/prod/test-cases/${containerId}/pin-version`,
        method: "PUT",
        params: { version_id: versionId },
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    deleteStagingPrompt: builder.mutation({
      query: ({ promptId }) => ({
        url: `/api/v1/scan_templates_v2/staging/prompts/${promptId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    deleteProdPrompt: builder.mutation({
      query: ({ promptId }) => ({
        url: `/api/v1/scan_templates_v2/prod/prompts/${promptId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),

    deleteStagingTestCases: builder.mutation({
      query: ({ containerId }) => ({
        url: `/api/v1/scan_templates_v2/staging/test-cases/${containerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StagingTemplates", "SyncStatus"],
    }),

    deleteProdTestCases: builder.mutation({
      query: ({ containerId }) => ({
        url: `/api/v1/scan_templates_v2/prod/test-cases/${containerId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ProdTemplates", "SyncStatus"],
    }),
  }),
});

export const {
  useGetStagingTemplatesQuery,
  useGetProdTemplatesQuery,
  useGetStagingTemplateDetailQuery,
  useGetProdTemplateDetailQuery,
  useGetSyncStatusQuery,
  useSyncStagingToProdMutation,
  useSyncProdToStagingMutation,
  useCreateStagingPromptMutation,
  useCreateProdPromptMutation,
  useCreateStagingPromptVersionMutation,
  useCreateProdPromptVersionMutation,
  usePinStagingVersionMutation,
  usePinProdVersionMutation,
  useUpdateStagingTemplateMutation,
  useUpdateProdTemplateMutation,
  useCreateStagingTemplateMutation,
  useCreateProdTemplateMutation,
  useDeleteStagingTemplateMutation,
  useDeleteProdTemplateMutation,
  useCreateStagingTestCasesMutation,
  useCreateProdTestCasesMutation,
  useCreateStagingTestCasesVersionMutation,
  useCreateProdTestCasesVersionMutation,
  usePinStagingTestCasesVersionMutation,
  usePinProdTestCasesVersionMutation,
  useDeleteStagingPromptMutation,
  useDeleteProdPromptMutation,
  useDeleteStagingTestCasesMutation,
  useDeleteProdTestCasesMutation,
} = scanTemplateSyncApiSlice;

